import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { BrewSensoryPublicPage } from "@/features/brew-sensory/brew-sensory-public-page";
import { TasterApp, TasterLinkError, TasterLoading } from "@/external/tasting/TasterApp";
import { TasterChannelProvider, useLiveTasterChannel } from "@/external/tasting/taster-channel";
import type { TasterData } from "@/external/tasting/taster-types";
import { LIVE_FALLBACK_MS, useTastingLive } from "@/external/tasting/use-tasting-live";

export const Route = createFileRoute("/sensory/$token")({
  component: PublicEvaluationPage,
});

function PublicEvaluationPage() {
  const { token } = Route.useParams();
  const { data, isLoading, error } = trpc.sensoryPublic.getByToken.useQuery(
    { token },
    {
      retry: false,
      // Brew sensory has no live state; only a tasting needs the fallback.
      refetchInterval: (q) => (q.state.data?.scope === "food" ? LIVE_FALLBACK_MS : false),
    },
  );

  if (isLoading) return <TasterLoading />;
  if (error || !data) return <TasterLinkError />;
  if (data.scope === "brew") return <BrewSensoryPublicPage token={token} />;
  return <LiveTasterApp token={token} data={data} />;
}

/** The tablet on the server: answers go in by the taster's token, and every change pings it. */
function LiveTasterApp({ token, data }: { token: string; data: TasterData }) {
  const utils = trpc.useUtils();
  const channel = useLiveTasterChannel(token);
  useTastingLive(data.session.id, () => utils.sensoryPublic.getByToken.invalidate({ token }));
  return (
    <TasterChannelProvider channel={channel}>
      <TasterApp data={data} />
    </TasterChannelProvider>
  );
}
